// Ui.Form.Group knows the label, whether the field is required, and which
// errors belong to it. The control is opaque Html handed to it, so in Elm
// nothing can travel between the two. This wires them in the DOM instead:
// `for`, `aria-describedby` and `aria-required` are set from what is actually
// inside the field, so no call site has to pass an id or remember a flag.

const LABELABLE = 'input:not([type="hidden"]), select, textarea';
const ERRORS = ".form-error";
const HELP = ".form-group__help";
const LABEL = "[data-field-label]";
const REQUIRED = "data-field-required";

// A select's own filter box lives inside its popup. It is a control, but not
// the one this field is about — the trigger beside it is.
const INSIDE_A_MENU = '[role="listbox"], [role="menu"]';

let fieldIds = 0;

const MANAGED_DESCRIPTION = /^elm-field-\d+-description-\d+$/;
const MANAGED_LABEL = /^elm-field-\d+-label$/;

function describe(control, descriptionIds) {
  // Ids we did not generate are the caller's and survive; the Set keeps a
  // preserved one from being appended again on every rewire.
  const ids = [
    ...new Set(
      (control.getAttribute("aria-describedby") ?? "")
        .split(" ")
        .filter((id) => id && !MANAGED_DESCRIPTION.test(id))
        .concat(descriptionIds),
    ),
  ];

  if (ids.length === 0) control.removeAttribute("aria-describedby");
  else control.setAttribute("aria-describedby", ids.join(" "));
}

// Only the name this component gave. A control named by anyone else keeps it.
function unname(control) {
  if (MANAGED_LABEL.test(control.getAttribute("aria-labelledby") ?? "")) {
    control.removeAttribute("aria-labelledby");
  }
}

class ElmField extends HTMLElement {
  connectedCallback() {
    this.fieldId ??= `elm-field-${++fieldIds}`;
    this.wire();
    // Elm re-renders the field's contents in place — an error appears, a
    // control swaps — and the wiring has to follow.
    this.observer = new MutationObserver(() => this.wire());
    this.observer.observe(this, { childList: true, subtree: true });
  }

  disconnectedCallback() {
    this.observer.disconnect();
  }

  own(selector) {
    return [...this.querySelectorAll(selector)].filter((el) => el.closest("elm-field") === this);
  }

  // The narrowest set of controls that contains this description: one, if the
  // description sits beside a single control, otherwise all of them.
  describedBy(node, controls) {
    for (let el = node.parentElement; el; el = el.parentElement) {
      const within = controls.filter((control) => el.contains(control));
      if (within.length === 1) return within;
      if (within.length > 1) return controls;
      if (el === this) break;
    }
    return controls;
  }

  wire() {
    const controls = this.own(LABELABLE).filter((el) => !el.closest(INSIDE_A_MENU));
    const described = new Map(controls.map((control) => [control, []]));

    this.own(`${ERRORS}, ${HELP}`).forEach((node, index) => {
      // Ours are positional and get reassigned on every wire, so a block
      // appearing earlier cannot take an id another block is still using.
      // An id we did not mint is the caller's and is left alone.
      if (!node.id || MANAGED_DESCRIPTION.test(node.id)) {
        node.id = `${this.fieldId}-description-${index}`;
      }
      this.describedBy(node, controls).forEach((control) => described.get(control).push(node.id));
    });

    controls.forEach((control) => describe(control, described.get(control)));

    // Group.required is a flag on the whole field, so it can only be spent on
    // a control when there is exactly one — on a group of them it would call
    // every part required, including the optional ones.
    const onControl = this.hasAttribute(REQUIRED) && controls.length === 1;
    controls.forEach((control) => control.removeAttribute("aria-required"));
    if (onControl) controls[0].setAttribute("aria-required", "true");

    const [label] = this.own(LABEL);
    if (!label) {
      this.removeAttribute("role");
      this.removeAttribute("aria-labelledby");
      controls.forEach(unname);
      return;
    }

    const [control] = controls;
    if (controls.length === 1 && !control.closest("label")) {
      control.id ||= `${this.fieldId}-control`;
      label.htmlFor = control.id;
      this.removeAttribute("role");
      this.removeAttribute("aria-labelledby");
      unname(control);
    } else {
      // Nothing to point `for` at, so the field becomes a named group and the
      // label names it rather than any one control inside.
      label.id ||= `${this.fieldId}-label`;
      label.removeAttribute("for");
      this.setAttribute("role", "group");
      this.setAttribute("aria-labelledby", label.id);
      controls.forEach(unname);
    }
  }
}

if (!customElements.get("elm-field")) {
  customElements.define("elm-field", ElmField);
}
